import { getReportContext, hasValue, numericValue, normalizeFindings } from './reportRules';

// ============================================================================
// TEXT GENERATOR WITH "DEFAULT TO NORMAL" LOGIC
// ============================================================================
// All ABCDE sections default to normal/unauffällig
// Only selected abnormalities override the normal state
// Text output is comprehensive, showing both normal and abnormal findings

// Base normal texts for ABCDE sections - These are the DEFAULT texts shown unless abnormalities are selected
const normalTexts = {
  airway: 'Atemweg frei und ohne Verlegung, keine Fremdkörper erkennbar, kein Zungenbiss, SH rosig',
  breathing: {
    spontaneous: 'Spontanatmung vorhanden und regelrecht',
    rate: 'Atemfrequenz normwertig',
    excursions: 'Thoraxexkursionen seitengleich',
    sounds: 'vesikuläres Atemgeräusch beidseits',
    dyspnea: 'keine Dyspnoe',
    cyanosis: 'keine Zyanose',
    auxiliary: 'kein Einsatz der Atemhilfsmuskulatur'
  },
  circulation: {
    stable: 'Kreislauf stabil',
    rhythm: 'Herzaktion rhythmisch',
    pulse: 'Puls kräftig und regelmäßig tastbar',
    cap_refill: 'Rekapillarisierungszeit unter 2 Sekunden',
    skin: 'Hautkolorit rosig und warm',
    bleeding: 'keine aktiven Blutungen',
    jugular: 'keine gestauten Halsvenen',
    edema: 'keine peripheren Ödeme',
    angina: 'keine Brustschmerzen'
  },
  disability: {
    consciousness: 'Patient wach, ansprechbar und POST orientiert',
    gcs: 'GCS 15',
    pupils: 'Pupillen rund, mittelweit, isokor und prompt lichtreagibel beidseits',
    motor: 'keine Lähmungserscheinungen',
    sensory: 'keine Sensibilitätsstörungen',
    seizure: 'kein Krampfgeschehen',
    befast: 'BEFAST: unauffällig',
    amnesia: 'keine Amnesie'
  },
  exposure: {
    injuries: 'Keine Verletzung',
    pain: 'keine Schmerzen',
    nausea: 'keine Übelkeit',
    vomiting: 'kein Erbrechen',
    temperature: 'Körpertemperatur normal',
    infection: 'kein Infekt'
  }
};

// Abnormality texts (these REPLACE or ADD to normal findings)
const abnormalityTexts = {
  airway: {
    airway_endangered: 'Atemwege gefährdet',
    partially_blocked: 'Atemweg teilweise verlegt',
    blocked: 'Atemweg vollständig verlegt',
    foreign_body: 'Fremdkörper im Atemweg sichtbar',
    intubated: 'Patient endotracheal intubiert, Tubus lagekontrolliert',
    tracheostomy: 'Trachealkanüle in situ',
    tongue_bite: 'Zungenbiss vorhanden',
    mucosa_pale: 'SH blass',
    mucosa_cyanotic: 'SH zyanotisch'
  },
  breathing: {
    increased_rate: 'Atemfrequenz erhöht (Tachypnoe)',
    decreased_rate: 'Atemfrequenz erniedrigt (Bradypnoe)',
    hyperventilation: 'Hyperventilation',
    dyspnea_mild: 'leichte Dyspnoe',
    dyspnea_moderate: 'mittelgradige Dyspnoe',
    dyspnea_severe: 'schwere Dyspnoe',
    cyanosis_yes: 'Zyanose erkennbar',
    vesicular_left: 'Atemgeräusch nur links',
    vesicular_right: 'Atemgeräusch nur rechts',
    auxiliary_muscles_yes: 'Einsatz der Atemhilfsmuskulatur',
    stridor: 'Stridor auskultierbar',
    rhonchi: 'Rasselgeräusche/Giemen bei Auskultation'
  },
  circulation: {
    pulse_irregular: 'Puls unregelmäßig',
    pulse_weak: 'Puls schwach und fadenförmig tastbar',
    tachycardia: 'Tachykardie (Pulsfrequenz erhöht)',
    bradycardia: 'Bradykardie (Pulsfrequenz erniedrigt)',
    hypertension: 'Hypertonie (Blutdruck erhöht)',
    hypotension: 'Hypotonie (Blutdruck erniedrigt)',
    skin_pale: 'Hautkolorit blass',
    skin_mottled: 'Haut marmoriert',
    skin_cyanotic: 'Haut zyanotisch',
    cap_refill_over2: 'Rekapillarisierungszeit verlängert (> 2 Sek.)',
    bleeding_yes: 'aktive Blutung vorhanden',
    cold_extremities: 'Akren kühl, kalte Extremitäten',
    jugular_distended: 'gestaute Halsvenen',
    edema_present: 'periphere Ödeme vorhanden',
    chest_pain: 'Brustschmerzen vorhanden'
  },
  disability: {
    pupils_anisokor: 'Pupillen seitenungleich (Anisokorie)',
    pupils_not_reactive: 'Pupillen nicht oder nur träge lichtreagibel',
    seizure_active: 'Patient krampft aktuell',
    seizure_past: 'Stattgehabter Krampfanfall',
    confusion: 'Patient verwirrt und desorientiert',
    unconscious: 'Patient bewusstlos',
    paralysis: 'Lähmungserscheinungen vorhanden',
    amnesia_present: 'Amnesie vorhanden',
    hypoglycemia: 'Hypoglykämie (BZ-Entgleisung)',
    hyperglycemia: 'Hyperglykämie (BZ-Entgleisung)'
  },
  exposure: {
    injuries_yes: 'Verletzungen vorhanden',
    pain_present: 'Schmerzen vorhanden',
    nausea_present: 'Übelkeit vorhanden',
    vomiting_present: 'Erbrechen vorhanden',
    fever: 'erhöhte Körpertemperatur',
    hypothermia: 'Hypothermie',
    infection_signs: 'Infektzeichen vorhanden',
    rash: 'Ausschläge vorhanden',
    swelling: 'Schwellungen vorhanden',
    deformity: 'Deformitäten vorhanden',
    bruising: 'Hämatome vorhanden',
    vertigo_rotational: 'Drehschwindel',
    vertigo_non_rotational: 'Schwankschwindel'
  }
};

// EKG findings
const ekgTexts = {
  sinus_rhythm: 'Sinusrhythmus',
  atrial_fibrillation: 'Vorhofflimmern',
  stemi: 'STEMI (ST-Hebungsinfarkt)',
  nstemi: 'NSTEMI-Verdacht',
  st_elevation: 'ST-Hebungen',
  st_depression: 'ST-Senkungen',
  rsb: 'Rechtsschenkelblock (RSB)',
  lsb: 'Linksschenkelblock (LSB)',
  av_block: 'AV-Block',
  extrasystoles: 'Extrasystolen',
  pacemaker: 'Schrittmacherrhythmus'
};

// SAMPLER texts
const samplerTexts = {
  symptoms: {
    chest_pain: 'Thoraxschmerzen',
    dyspnea: 'Dyspnoe',
    nausea: 'Übelkeit',
    dizziness: 'Schwindel',
    pain: 'Schmerz',
    psychological_distress: 'Psychische Ausnahmesituation',
    weakness: 'Schwäche',
    confusion: 'Verwirrtheit'
  },
  allergies: {
    known: 'Bekannte Allergien vorhanden',
    none: 'Keine Allergien bekannt',
    unknown: 'Allergien unbekannt'
  },
  medications: {
    as_prescribed: 'Dauermedikation regelmäßig eingenommen',
    none: 'Keine Dauermedikation',
    irregular: 'Unregelmäßige Medikamenteneinnahme',
    unknown: 'Medikation unbekannt'
  },
  history: {
    diabetes: 'Diabetes mellitus',
    chd: 'KHK',
    copd: 'COPD',
    asthma: 'Asthma bronchiale',
    hypertension: 'Arterielle Hypertonie',
    heart_failure: 'Herzinsuffizienz',
    stroke: 'Z.n. Schlaganfall',
    atrial_fibrillation: 'Vorhofflimmern',
    renal_insufficiency: 'Niereninsuffizienz',
    epilepsy: 'Epilepsie',
    dementia: 'Demenz',
    cancer: 'Tumorerkrankung',
    depression: 'Depression',
    thyroid: 'Schilddrüsenerkrankung',
    liver_disease: 'Lebererkrankung',
    rheumatism: 'Rheumatische Erkrankung',
    none: 'Keine Vorerkrankungen'
  },
  event: {
    fall: 'Sturz',
    chest_pain: 'Thoraxschmerz',
    dyspnea: 'Dyspnoe',
    dizziness: 'Schwindel',
    collapse: 'Synkope',
    trauma: 'Trauma',
    stroke_suspected: 'Verdacht auf Schlaganfall',
    seizure: 'Krampfanfall',
    accident: 'Verkehrsunfall'
  },
  riskFactors: {
    nicotine: 'Nikotinabusus',
    alcohol: 'Alkoholkonsum',
    obesity: 'Adipositas',
    drug_abuse: 'Drogenkonsum',
    sedentary: 'Bewegungsmangel',
    stress: 'Chronischer Stress',
    none: 'Keine Risikofaktoren'
  }
};

// STU texts - now with NORMAL defaults
const stuTexts = {
  // Normal findings (default)
  head_normal: 'Kopf unauffällig',
  neck_normal: 'HWS unauffällig',
  chest_normal: 'Thorax stabil',
  abdomen_normal: 'Abdomen weich',
  pelvis_normal: 'Becken stabil',
  spine_normal: 'Wirbelsäule unauffällig',
  extremities_normal: 'Extremitäten regelrecht',
  
  // Abnormal findings
  head_injury: 'Kopf: Verletzung/Hämatom feststellbar',
  neck_pain: 'HWS: Druckschmerz',
  chest_injury: 'Thorax: Instabilität/Schmerzen',
  abdomen_pain: 'Abdomen: Druckschmerz/Abwehrspannung',
  pelvis_unstable: 'Becken: Instabilität',
  spine_pain: 'Wirbelsäule: Druckschmerz',
  extremities_injury: 'Extremitäten: Deformität/Funktionseinschränkung'
};

// Additional texts - AUSFÜHRLICH
const additionalTexts = {
  ivAccess: 'Ein venöser Zugang wurde nach umfassender Aufklärung über Risiken und Nutzen der Maßnahme sowie nach Einholung der Einwilligung des Patienten gelegt.',
  ambulatoryCare: 'Nach Untersuchung und Behandlung verbleibt der Patient am Einsatzort. Zum Zeitpunkt der abschließenden Beurteilung ist ein Transport mit dem Rettungsdienst nicht erforderlich. Der Patient wurde über die erhobenen Befunde, das weitere Vorgehen und Warnzeichen aufgeklärt. Auf alternative Versorgungsmöglichkeiten durch den Hausarzt oder den kassenärztlichen Bereitschaftsdienst (116117) wurde hingewiesen. Bei einer Verschlechterung des Zustands oder neu auftretenden akuten Beschwerden wurde die erneute Verständigung des Rettungsdienstes über die Notrufnummer 112 empfohlen.',
  transportRefusal: 'Nach ausführlicher medizinischer Beratung und Aufklärung über seinen aktuellen Gesundheitszustand sowie die möglichen Risiken und Folgen einer fehlenden weiteren medizinischen Versorgung lehnte der Patient den Transport in ein Krankenhaus ab. Dem Patienten wurde eindringlich die Notwendigkeit einer ärztlichen Untersuchung und Behandlung nahegelegt. Es wurde darauf hingewiesen, dass die Möglichkeiten präklinisch mit dem Rettungswagen begrenzt sind und eine ausführliche Untersuchung nur im Krankenhaus erfolgen kann. Es wurde auf potenzielle Gefahren und Komplikationen hingewiesen, die durch das Unterlassen einer medizinischen Weiterversorgung entstehen können. Trotz dieser Aufklärung entschied sich der Patient bewusst gegen den Transport und verbleibt auf eigene Verantwortung am Einsatzort. Der Patient wurde darauf hingewiesen, bei Verschlechterung des Zustandes oder neu auftretenden Symptomen umgehend den Rettungsdienst zu verständigen.'
};

// Measures texts
const measuresTexts = {
  oxygen_mask: 'Sauerstoffgabe per Maske',
  oxygen_nasal: 'Sauerstoffgabe per Nasensonde',
  wound_care: 'Wundversorgung durchgeführt',
  fracture_splint: 'Fraktur geschient',
  vacuum_mattress: 'Patient auf Vakuummatratze gelagert',
  cervical_collar: 'HWS-Schiene (Stifneck) angelegt',
  iv_access: 'Venöser Zugang gelegt',
  medication_given: 'Medikamentengabe'
};

// ============================================================================
// MAIN TEXT GENERATION FUNCTION
// ============================================================================

export const generateAnamnesisText = (formData) => {
  let sections = [];
  const context = getReportContext(formData);

  // ========== INITIAL SITUATION ==========
  const initialSection = generateInitialSituation(formData.initial);
  if (initialSection) sections.push(initialSection);

  // ========== ABCDE SECTIONS ==========
  const abcdeSection = generateABCDE(formData.abcde, context);
  if (abcdeSection) sections.push(abcdeSection);

  // ========== SAMPLER SECTIONS ==========
  const samplerSection = generateSAMPLER(formData.sampler);
  if (samplerSection) sections.push(samplerSection);

  // ========== STU SECTION ==========
  const stuSection = generateSTU(formData.stu);
  if (stuSection) sections.push(stuSection);

  // ========== MEASURES SECTION ==========
  const measuresSection = generateMeasures(formData.measures);
  if (measuresSection) sections.push(measuresSection);

  // ========== ADDITIONAL INFO ==========
  const additionalSection = generateAdditional(formData.additional, context);
  if (additionalSection) sections.push(additionalSection);

  return sections.join('\n') || 'Keine Daten vorhanden.';
};

// ============================================================================
// SECTION GENERATORS
// ============================================================================

function generateInitialSituation(data) {
  if (!data) return '';
  
  let parts = [];
  const selections = normalizeFindings(data.selections || [], 'initial');
  
  // Map selections to readable text
  const textMap = {
    opens_door: 'Patient öffnete selbstständig die Tür',
    in_bed: 'Patient im Bett liegend angetroffen',
    on_chair: 'Patient sitzend auf Stuhl/Sofa',
    awake: 'wach und ansprechbar',
    responds: 'reagiert auf Ansprache'
  };
  
  const selectedTexts = selections.map(id => textMap[id]).filter(Boolean);
  
  if (selectedTexts.length > 0 || data.freeText) {
    let text = 'Patient wurde wie folgt angetroffen:\n';
    if (selectedTexts.length > 0) {
      text += selectedTexts.join(', ') + '.';
    }
    if (data.freeText) {
      text += (selectedTexts.length > 0 ? ' ' : '') + data.freeText;
    }
    parts.push(text);
  }
  
  return parts.length > 0 ? parts.join(' ') + '\n' : '';
}

function generateABCDE(data, context) {
  if (!data) return '';
  
  let text = 'ABCDE:\n';
  
  // A - Airway
  text += generateAirway(data.airway, context);
  text += '\n';
  
  // B - Breathing  
  text += generateBreathing(data.breathing, context);
  text += '\n';
  
  // C - Circulation
  text += generateCirculation(data.circulation, context);
  text += '\n';
  
  // D - Disability
  text += generateDisability(data.disability, context);
  text += '\n';
  
  // E - Exposure
  text += generateExposure(data.exposure, context);
  text += '\n';
  
  return text;
}

function generateAirway(data = {}, context = {}) {
  const abnormalities = normalizeFindings(data.abnormalities, 'airway');
  
  // Build text with abnormalities, replacing conflicting normal parts
  let parts = [];
  
  // Check for airway issues
  const hasEndangered = abnormalities.includes('airway_endangered');
  const hasObstruction = abnormalities.some(a => ['partially_blocked', 'blocked'].includes(a));
  const hasForeignBody = abnormalities.includes('foreign_body');
  const hasIntubation = abnormalities.includes('intubated');
  const hasTrach = abnormalities.includes('tracheostomy');
  const hasMucosaPale = abnormalities.includes('mucosa_pale');
  const hasMucosaCyanotic = abnormalities.includes('mucosa_cyanotic');
  
  if (hasEndangered) {
    parts.push(abnormalityTexts.airway.airway_endangered);
  }
  if (hasObstruction) {
    // Add obstruction texts
    if (abnormalities.includes('blocked')) {
      parts.push(abnormalityTexts.airway.blocked);
    } else if (abnormalities.includes('partially_blocked')) {
      parts.push(abnormalityTexts.airway.partially_blocked);
    }
  } else if (!hasEndangered && !hasForeignBody && !hasIntubation && !hasTrach) {
    // Airway free
    parts.push('Atemweg frei und ohne Verlegung');
  }
  if (hasIntubation) parts.push(abnormalityTexts.airway.intubated);
  if (hasTrach) parts.push(abnormalityTexts.airway.tracheostomy);
  
  // Foreign body
  if (hasForeignBody) {
    parts.push(abnormalityTexts.airway.foreign_body);
  } else {
    parts.push('keine Fremdkörper erkennbar');
  }
  
  // Tongue bite - always check
  parts.push(abnormalities.includes('tongue_bite') ? abnormalityTexts.airway.tongue_bite : 'kein Zungenbiss');
  
  // Mucosa color
  if (hasMucosaPale) {
    parts.push(abnormalityTexts.airway.mucosa_pale);
  }
  if (hasMucosaCyanotic) {
    parts.push(abnormalityTexts.airway.mucosa_cyanotic);
  } else if (!hasMucosaPale && !context.anyCyanosis) {
    parts.push('SH rosig');
  }
  
  return 'A: ' + parts.join(', ') + '.';
}

function generateBreathing(data = {}, context = {}) {
  const abnormalities = normalizeFindings(data.abnormalities, 'breathing');
  let parts = [];
  
  // Identify abnormalities
  const hasIncreasedRate = abnormalities.includes('increased_rate');
  const hasDecreasedRate = abnormalities.includes('decreased_rate');
  const hasHyperventilation = abnormalities.includes('hyperventilation');
  const hasDyspnea = abnormalities.some(a => a.startsWith('dyspnea_')) || context.dyspnea;
  const hasCyanosis = abnormalities.includes('cyanosis_yes') || context.cyanosis;
  const hasVesicularIssue = abnormalities.includes('vesicular_left') || abnormalities.includes('vesicular_right');
  const hasAuxiliary = abnormalities.includes('auxiliary_muscles_yes');
  const hasStridor = abnormalities.includes('stridor');
  const hasRhonchi = abnormalities.includes('rhonchi');
  
  const rate = numericValue(data.respiratory_rate);
  const spo2 = numericValue(data.spo2);
  const breathingConcern = abnormalities.length > 0 || hasDyspnea || hasCyanosis ||
    context.airwaySupport || context.chestInjury || context.symptomDetails ||
    (rate !== undefined && (rate < 12 || rate > 20)) || (spo2 !== undefined && spo2 < 94);
  if (!breathingConcern) parts.push(normalTexts.breathing.spontaneous);
  
  // 2. Respiratory rate
  if (hasIncreasedRate) {
    parts.push(abnormalityTexts.breathing.increased_rate);
  } else if (hasDecreasedRate) {
    parts.push(abnormalityTexts.breathing.decreased_rate);
  } else if (rate === 0) {
    parts.push('keine Spontanatmung (Atemfrequenz 0/min)');
  } else if (rate !== undefined) {
    parts.push(`Atemfrequenz ${data.respiratory_rate}/min`);
  } else if (!hasHyperventilation) {
    parts.push(normalTexts.breathing.rate);
  }
  if ((hasIncreasedRate || hasDecreasedRate) && hasValue(data.respiratory_rate)) {
    parts.push(`Atemfrequenz ${data.respiratory_rate}/min`);
    if ((hasIncreasedRate && rate <= 20) || (hasDecreasedRate && rate >= 12)) {
      parts.push('Angaben prüfen: Atemfrequenz und ausgewählter Befund widersprechen sich');
    }
  }
  
  // Hyperventilation (zusätzlich)
  if (hasHyperventilation) {
    parts.push(abnormalityTexts.breathing.hyperventilation);
  }
  
  // 3. Thorax excursions (always normal unless specific issue)
  if (!context.chestInjury && !context.airwaySupport && rate !== 0) parts.push(normalTexts.breathing.excursions);
  
  // 4. Breath sounds
  if (hasVesicularIssue && !hasStridor && !hasRhonchi) {
    if (abnormalities.includes('vesicular_left')) {
      parts.push(abnormalityTexts.breathing.vesicular_left);
    } else if (abnormalities.includes('vesicular_right')) {
      parts.push(abnormalityTexts.breathing.vesicular_right);
    }
  } else if (hasVesicularIssue) {
    parts.push(abnormalities.includes('vesicular_left') ? 'Atemgeräusch nur links' : 'Atemgeräusch nur rechts');
  } else if (!breathingConcern) {
    parts.push(normalTexts.breathing.sounds);
  }
  
  // 5. Dyspnea
  if (hasDyspnea) {
    const dyspneaTypes = abnormalities.filter(a => a.startsWith('dyspnea_'));
    dyspneaTypes.forEach(type => {
      if (abnormalityTexts.breathing[type]) {
        parts.push(abnormalityTexts.breathing[type]);
      }
    });
    if (dyspneaTypes.length === 0) parts.push('Dyspnoe vorhanden');
  } else if (!context.impaired && !context.symptomDetails) {
    parts.push(normalTexts.breathing.dyspnea);
  }
  
  // 6. Cyanosis
  if (hasCyanosis) {
    parts.push(abnormalityTexts.breathing.cyanosis_yes);
  } else {
    parts.push(normalTexts.breathing.cyanosis);
  }
  
  // 7. Auxiliary muscles
  if (hasAuxiliary) {
    parts.push(abnormalityTexts.breathing.auxiliary_muscles_yes);
  } else {
    parts.push(normalTexts.breathing.auxiliary);
  }
  
  // 8. Additional findings (only if present)
  if (hasStridor) parts.push(abnormalityTexts.breathing.stridor);
  if (hasRhonchi) parts.push(abnormalityTexts.breathing.rhonchi);
  
  // 9. SpO2 - always show
  if (hasValue(data.spo2) || !hasCyanosis) {
    const spo2Value = hasValue(data.spo2) ? data.spo2 : 98;
    parts.push(`Sättigung: ${spo2Value}%${context.oxygen ? '' : ' unter Raumluft'}`);
  }
  
  return 'B: ' + parts.join(', ') + '.';
}

function generateCirculation(data = {}, context = {}) {
  const abnormalities = normalizeFindings(data.abnormalities, 'circulation');
  const ekg = normalizeFindings(data.ekg, 'ekg');
  let parts = [];
  
  // Identify key abnormalities
  const hasIrregular = abnormalities.includes('pulse_irregular') || ekg.includes('atrial_fibrillation') || ekg.includes('extrasystoles');
  const hasTachy = abnormalities.includes('tachycardia');
  const hasBrady = abnormalities.includes('bradycardia');
  const hasWeak = abnormalities.includes('pulse_weak');
  const hasHypertension = abnormalities.includes('hypertension');
  const hasHypotension = abnormalities.includes('hypotension');
  const hasSkinPale = abnormalities.includes('skin_pale');
  const hasSkinMottled = abnormalities.includes('skin_mottled');
  const hasSkinCyanotic = abnormalities.includes('skin_cyanotic');
  const hasCapRefillIssue = abnormalities.includes('cap_refill_over2');
  const hasBleeding = abnormalities.includes('bleeding_yes');
  const hasColdExtremities = abnormalities.includes('cold_extremities');
  const hasJugularDistended = abnormalities.includes('jugular_distended');
  const hasEdema = abnormalities.includes('edema_present');
  const hasChestPain = abnormalities.includes('chest_pain') || context.chestPain;
  const heartRate = numericValue(data.heart_rate);
  const bpSys = numericValue(data.bp_systolic);
  const bpDia = numericValue(data.bp_diastolic);
  
  // 1. Circulation stability (always)
  if (!abnormalities.length && !ekg.some(id => id !== 'sinus_rhythm') && !hasChestPain &&
      !context.symptomDetails && !context.anyCyanosis &&
      !(heartRate < 60 || heartRate > 100 || bpSys < 90 || bpSys >= 140 || bpDia >= 90)) {
    parts.push(normalTexts.circulation.stable);
  }
  
  // 2. Cardiac rhythm - CRITICAL: if irregular, don't say "rhythmisch"
  if (hasIrregular) {
    parts.push(abnormalityTexts.circulation.pulse_irregular);
  } else {
    parts.push(normalTexts.circulation.rhythm);
  }
  
  // 3. Pulse quality - CRITICAL: avoid contradiction
  if (hasWeak) {
    parts.push(abnormalityTexts.circulation.pulse_weak);
  } else if (!hasIrregular) {
    // Only add "kräftig und regelmäßig" if pulse is not irregular
    parts.push(normalTexts.circulation.pulse);
  }
  
  // 4. Pulse frequency
  if (hasTachy) {
    parts.push(abnormalityTexts.circulation.tachycardia);
  } else if (hasBrady) {
    parts.push(abnormalityTexts.circulation.bradycardia);
  }
  
  // 5. Heart rate value
  if (hasValue(data.heart_rate) || (!hasTachy && !hasBrady)) {
    parts.push(`Herzfrequenz ${hasValue(data.heart_rate) ? data.heart_rate : 75}`);
  }
  if ((hasTachy && heartRate <= 100) || (hasBrady && heartRate >= 60)) {
    parts.push('Angaben prüfen: Herzfrequenz und ausgewählter Befund widersprechen sich');
  }
  
  // 6. Cap refill
  if (hasCapRefillIssue) {
    parts.push(abnormalityTexts.circulation.cap_refill_over2);
  } else {
    parts.push(normalTexts.circulation.cap_refill);
  }
  
  // 7. Skin color - CRITICAL: only one condition
  if (hasSkinPale) {
    parts.push(abnormalityTexts.circulation.skin_pale);
  }
  if (hasSkinMottled) {
    parts.push(abnormalityTexts.circulation.skin_mottled);
  }
  if (hasSkinCyanotic) {
    parts.push(abnormalityTexts.circulation.skin_cyanotic);
  } else if (!hasSkinPale && !hasSkinMottled && !context.anyCyanosis) {
    parts.push(hasColdExtremities ? 'Hautkolorit rosig' : normalTexts.circulation.skin);
  }
  
  // 8. Cold extremities (only if present)
  if (hasColdExtremities) {
    parts.push(abnormalityTexts.circulation.cold_extremities);
  }
  
  // 9. Bleeding
  if (hasBleeding) {
    parts.push(abnormalityTexts.circulation.bleeding_yes);
  } else {
    parts.push(normalTexts.circulation.bleeding);
  }
  
  // 10. Blood pressure - always show
  // Add hypertension/hypotension note if present
  if (hasHypertension) {
    parts.push(abnormalityTexts.circulation.hypertension);
  } else if (hasHypotension) {
    parts.push(abnormalityTexts.circulation.hypotension);
  }
  
  if (hasValue(data.bp_systolic) || hasValue(data.bp_diastolic)) {
    parts.push(`RR: ${hasValue(data.bp_systolic) ? data.bp_systolic : '–'}/${hasValue(data.bp_diastolic) ? data.bp_diastolic : '–'} mmHg`);
  } else if (!hasHypertension && !hasHypotension) {
    parts.push('RR: 120/80 mmHg');
  }
  if ((hasHypertension && bpSys !== undefined && bpDia !== undefined && bpSys < 140 && bpDia < 90) ||
      (hasHypotension && bpSys !== undefined && bpSys >= 90)) {
    parts.push('Angaben prüfen: Blutdruck und ausgewählter Befund widersprechen sich');
  }
  
  // 11. Jugular veins
  if (hasJugularDistended) {
    parts.push(abnormalityTexts.circulation.jugular_distended);
  } else {
    parts.push(normalTexts.circulation.jugular);
  }
  
  // 12. Peripheral edema
  if (hasEdema) {
    parts.push(abnormalityTexts.circulation.edema_present);
  } else {
    parts.push(normalTexts.circulation.edema);
  }
  
  // 13. Chest pain (Brustschmerzen)
  if (hasChestPain) {
    parts.push(abnormalityTexts.circulation.chest_pain);
  } else if (!context.impaired && !context.symptomDetails) {
    parts.push(normalTexts.circulation.angina);
  }
  
  // 14. EKG findings (only if present)
  if (ekg.length > 0) {
    const ekgFindings = ekg.map(id => ekgTexts[id]).filter(Boolean);
    if (ekgFindings.length > 0) {
      parts.push('EKG: ' + ekgFindings.join(', '));
    }
  }
  
  return 'C: ' + parts.join(', ') + '.';
}

function generateDisability(data = {}, context = {}) {
  const abnormalities = normalizeFindings(data.abnormalities, 'disability');
  const orientations = data.orientations || ['oriented_place', 'oriented_time', 'oriented_person', 'oriented_situation'];
  const befast = data.befast || ['balance', 'eyes', 'face', 'arms', 'speech'];
  let parts = [];
  
  // Identify key abnormalities
  const isUnconscious = abnormalities.includes('unconscious');
  const isConfused = abnormalities.includes('confusion') || context.confusion;
  const hasPupilAnisokor = abnormalities.includes('pupils_anisokor');
  const hasPupilNotReactive = abnormalities.includes('pupils_not_reactive');
  const hasSeizureActive = abnormalities.includes('seizure_active');
  const hasSeizurePast = abnormalities.includes('seizure_past');
  const hasParalysis = abnormalities.includes('paralysis');
  const hasAmnesia = abnormalities.includes('amnesia_present');
  const hasHypoglycemia = abnormalities.includes('hypoglycemia');
  const hasHyperglycemia = abnormalities.includes('hyperglycemia');
  const gcs = numericValue(data.gcs);
  const allOrientations = ['oriented_place', 'oriented_time', 'oriented_person', 'oriented_situation'];
  const missingOrientations = allOrientations.filter(o => !orientations.includes(o));
  const cannotAssess = isUnconscious || hasSeizureActive;
  
  // 1. Consciousness - CRITICAL: check contradictions
  if (isUnconscious) {
    parts.push(abnormalityTexts.disability.unconscious);
  } else if (hasSeizureActive) {
    // No automatic normal consciousness/orientation during an active seizure.
  } else if (isConfused) {
    parts.push(abnormalityTexts.disability.confusion);
  } else if (gcs !== undefined && gcs < 15) {
    parts.push('Bewusstseinslage eingeschränkt');
  } else if (missingOrientations.length === 0 && !context.symptomDetails) {
    parts.push(normalTexts.disability.consciousness);
  } else if (!context.symptomDetails) {
    parts.push('Patient wach und ansprechbar');
  }
  
  // 2. Orientation (only if conscious and not confused)
  if (!cannotAssess && !isConfused) {
    if (missingOrientations.length > 0) {
      const orientationNames = {
        oriented_place: 'örtlich',
        oriented_time: 'zeitlich',
        oriented_person: 'zur Person',
        oriented_situation: 'situativ'
      };
      const missing = missingOrientations.map(o => orientationNames[o]).join(', ');
      parts.push(`nicht ${missing} orientiert`);
    }
  }
  
  // 3. GCS - always show
  if (hasValue(data.gcs)) {
    parts.push(`GCS ${data.gcs}`);
    if (gcs === 15 && (cannotAssess || isConfused || missingOrientations.length > 0)) {
      parts.push('Angaben prüfen: GCS 15 und neurologischer Befund widersprechen sich');
    }
  } else if (!cannotAssess && !isConfused && !missingOrientations.length && !context.symptomDetails) {
    parts.push(normalTexts.disability.gcs);
  }
  
  // 4. Pupils - CRITICAL: avoid contradictions
  if (hasPupilAnisokor) {
    parts.push(abnormalityTexts.disability.pupils_anisokor);
  }
  if (hasPupilNotReactive) {
    parts.push(abnormalityTexts.disability.pupils_not_reactive);
  } else if (!hasPupilAnisokor) {
    parts.push(normalTexts.disability.pupils);
  }
  
  // 5. Motor and sensory - CRITICAL: check for paralysis
  if (hasParalysis) {
    parts.push(abnormalityTexts.disability.paralysis);
  } else if (!cannotAssess && befast.includes('arms') && befast.includes('face') && !context.symptomDetails) {
    parts.push(normalTexts.disability.motor);
  }
  if (!cannotAssess && !hasParalysis && !context.symptomDetails) {
    parts.push(normalTexts.disability.sensory);
  }
  
  // 6. Seizure
  if (hasSeizureActive) {
    parts.push(abnormalityTexts.disability.seizure_active);
  }
  if (hasSeizurePast) {
    parts.push(abnormalityTexts.disability.seizure_past);
  } else if (!hasSeizureActive && !context.seizure && !context.symptomDetails) {
    parts.push(normalTexts.disability.seizure);
  }
  
  // 7. Blood sugar (if provided) + BZ-Entgleisung
  if (hasHypoglycemia) {
    parts.push(abnormalityTexts.disability.hypoglycemia);
  } else if (hasHyperglycemia) {
    parts.push(abnormalityTexts.disability.hyperglycemia);
  }
  
  if (hasValue(data.blood_sugar)) {
    parts.push(`BZ ${data.blood_sugar} mg/dl`);
    const sugar = numericValue(data.blood_sugar);
    if ((hasHypoglycemia && sugar >= 70) || (hasHyperglycemia && sugar < 140)) {
      parts.push('Angaben prüfen: Blutzucker und ausgewählter Befund widersprechen sich');
    }
  }
  
  // 8. BEFAST - simplified format
  const befastAll = ['balance', 'eyes', 'face', 'arms', 'speech'];
  const befastNames = {
    balance: 'Balance',
    eyes: 'Augen',
    face: 'Face',
    arms: 'Arme',
    speech: 'Sprache'
  };
  
  // Check if all BEFAST items are normal
  const allBefastNormal = befastAll.every(item => befast.includes(item));
  
  if (cannotAssess) {
    parts.push('BEFAST: nicht zuverlässig beurteilbar');
    const explicitAbnormal = befastAll.filter(item => !befast.includes(item));
    if (explicitAbnormal.length) parts.push('Dokumentierte BEFAST-Auffälligkeiten: ' + explicitAbnormal.map(item => befastNames[item]).join(', '));
  } else if (allBefastNormal && !hasParalysis && !context.symptomDetails && !(gcs < 15)) {
    parts.push('BEFAST: unauffällig');
  } else {
    // Show individual results
    const befastResults = befastAll.map(item => {
      const isNormal = befast.includes(item);
      if (isNormal && ((hasParalysis && ['face', 'arms'].includes(item)) || context.symptomDetails || gcs < 15)) return null;
      return `${befastNames[item]} ${isNormal ? 'unauffällig' : 'auffällig'}`;
    });
    if (befastResults.filter(Boolean).length) parts.push('BEFAST: ' + befastResults.filter(Boolean).join(', '));
  }
  
  // 9. Symptom onset (if provided)
  if (data.symptom_onset) {
    parts.push(`Symptombeginn ${data.symptom_onset}`);
  }
  
  // 10. Amnesia
  if (hasAmnesia) {
    parts.push(abnormalityTexts.disability.amnesia_present);
  } else if (!cannotAssess && !isConfused && !(gcs < 15) && !context.symptomDetails) {
    parts.push(normalTexts.disability.amnesia);
  }
  
  return 'D: ' + parts.join(', ') + '.';
}

function generateExposure(data = {}, context = {}) {
  const abnormalities = data.abnormalities || [];
  let parts = [];
  
  // Identify key abnormalities
  const hasInjuries = abnormalities.includes('injuries_yes');
  const hasNausea = abnormalities.includes('nausea') || abnormalities.includes('nausea_present') || context.nausea;
  const hasVomiting = abnormalities.includes('vomiting') || abnormalities.includes('vomiting_present');
  const hasVertigoRotational = abnormalities.includes('vertigo_rotational');
  const hasVertigoNonRotational = abnormalities.includes('vertigo_non_rotational');
  
  // 1. Injuries
  if (hasInjuries) {
    parts.push(abnormalityTexts.exposure.injuries_yes);
  } else if (!context.injuries) {
    parts.push(normalTexts.exposure.injuries);
  }
  ['rash', 'swelling', 'deformity', 'bruising'].forEach(id => {
    if (abnormalities.includes(id)) parts.push(abnormalityTexts.exposure[id]);
  });
  
  // 2. Pain
  if (data.pain_location) {
    parts.push(`Schmerzen: ${data.pain_location}`);
    if (numericValue(data.pain_scale) > 0) {
      parts.push(`Schmerzskala ${data.pain_scale}/10`);
    }
  } else if (numericValue(data.pain_scale) > 0) {
    parts.push(`Schmerzen vorhanden, Schmerzskala ${data.pain_scale}/10`);
  } else if (abnormalities.includes('pain_present')) {
    parts.push(abnormalityTexts.exposure.pain_present);
  } else if (!context.pain && !context.impaired && !context.symptomDetails) {
    parts.push(normalTexts.exposure.pain);
  }
  if (hasValue(data.pain_scale) && numericValue(data.pain_scale) === 0 && (data.pain_location || context.pain || abnormalities.includes('pain_present'))) {
    parts.push('Angaben prüfen: Schmerzskala 0/10 trotz dokumentierter Schmerzen');
  }
  
  // 3. Nausea
  if (hasNausea) {
    parts.push(abnormalityTexts.exposure.nausea_present);
  } else if (!context.impaired && !context.symptomDetails) {
    parts.push(normalTexts.exposure.nausea);
  }
  
  // 4. Vomiting
  if (hasVomiting) {
    parts.push(abnormalityTexts.exposure.vomiting_present);
  } else if (!context.symptomDetails) {
    parts.push(normalTexts.exposure.vomiting);
  }
  
  // 5. Temperature
  if (hasValue(data.temperature)) {
    const temp = numericValue(data.temperature);
    if (temp > 38.0) {
      parts.push(`Körpertemperatur erhöht: ${data.temperature}°C`);
    } else if (temp < 36.0) {
      parts.push(`Körpertemperatur erniedrigt: ${data.temperature}°C (Hypothermie)`);
    } else {
      parts.push(`Körpertemperatur: ${data.temperature}°C`);
    }
  } else if (abnormalities.includes('fever')) {
    parts.push(abnormalityTexts.exposure.fever);
  } else if (abnormalities.includes('hypothermia')) {
    parts.push(abnormalityTexts.exposure.hypothermia);
  } else {
    parts.push(normalTexts.exposure.temperature);
  }
  
  // 6. Vertigo (Schwindel)
  if (hasVertigoRotational) {
    parts.push(abnormalityTexts.exposure.vertigo_rotational);
  }
  if (hasVertigoNonRotational) {
    parts.push(abnormalityTexts.exposure.vertigo_non_rotational);
  }
  
  // 7. Infection
  if (abnormalities.includes('infection_signs')) {
    parts.push(abnormalityTexts.exposure.infection_signs);
  } else if (!(numericValue(data.temperature) > 38) && !abnormalities.includes('fever') && !context.symptomDetails) {
    parts.push(normalTexts.exposure.infection);
  }
  
  return 'E: ' + parts.join(', ') + '.';
}

function generateSAMPLER(data) {
  if (!data) return '';
  
  let parts = [];
  
  // S - Symptoms
  const symptoms = (data.symptoms || []).map(id => samplerTexts.symptoms[id]).filter(Boolean);
  if (symptoms.length > 0 || data.symptoms_freetext) {
    let text = 'S - Symptome: ' + symptoms.join(', ');
    if (data.symptoms_freetext) {
      text += (symptoms.length > 0 ? ', ' : '') + data.symptoms_freetext;
    }
    parts.push(text);
  }
  
  // A - Allergies
  const allergies = normalizeFindings(data.allergies, 'allergies')
    .filter(id => !data.allergies_freetext?.trim() || id === 'known')
    .map(id => samplerTexts.allergies[id]).filter(Boolean);
  if (allergies.length > 0 || data.allergies_freetext) {
    let text = 'A - Allergien: ' + allergies.join(', ');
    if (data.allergies_freetext) {
      text += (allergies.length > 0 ? ', ' : '') + data.allergies_freetext;
    }
    parts.push(text);
  }
  
  // M - Medications
  const medications = normalizeFindings(data.medications, 'medications')
    .filter(id => !data.medications_freetext?.trim() || !['none', 'unknown'].includes(id))
    .map(id => samplerTexts.medications[id]).filter(Boolean);
  if (medications.length > 0 || data.medications_freetext) {
    let text = 'M - Medikamente: ' + medications.join(', ');
    if (data.medications_freetext) {
      text += (medications.length > 0 ? ', ' : '') + data.medications_freetext;
    }
    parts.push(text);
  }
  
  // P - Past medical history
  const history = normalizeFindings(data.history, 'history')
    .filter(id => !data.history_freetext?.trim() || id !== 'none')
    .map(id => samplerTexts.history[id]).filter(Boolean);
  if (history.length > 0 || data.history_freetext) {
    let text = 'P - Patientenvorgeschichte: ' + history.join(', ');
    if (data.history_freetext) {
      text += (history.length > 0 ? ', ' : '') + data.history_freetext;
    }
    parts.push(text);
  }
  
  // L - Last meal
  if (data.lastMeal || data.lastMeal_freetext) {
    let text = 'L - Letzte Mahlzeit: ';
    if (data.lastMeal) text += data.lastMeal;
    if (data.lastMeal_freetext) {
      text += (data.lastMeal ? ', ' : '') + data.lastMeal_freetext;
    }
    parts.push(text);
  }
  
  // E - Event
  const events = (data.event || []).map(id => samplerTexts.event[id]).filter(Boolean);
  if (events.length > 0 || data.event_freetext) {
    let text = 'E - Ereignis: ' + events.join(', ');
    if (data.event_freetext) {
      text += (events.length > 0 ? ', ' : '') + data.event_freetext;
    }
    parts.push(text);
  }
  
  // R - Risk factors
  const risks = normalizeFindings(data.riskFactors, 'riskFactors')
    .filter(id => !data.riskFactors_freetext?.trim() || id !== 'none')
    .map(id => samplerTexts.riskFactors[id]).filter(Boolean);
  if (risks.length > 0 || data.riskFactors_freetext) {
    let text = 'R - Risikofaktoren: ' + risks.join(', ');
    if (data.riskFactors_freetext) {
      text += (risks.length > 0 ? ', ' : '') + data.riskFactors_freetext;
    }
    parts.push(text);
  }
  
  // Weight
  if (data.weight) {
    parts.push(`Patientengewicht: ${data.weight} kg`);
  }
  
  return parts.length > 0 ? '\nSAMPLER:\n' + parts.join('\n') + '\n' : '';
}

function generateSTU(data) {
  if (!data || !data.enabled) {
    return '';
  }
  
  const findings = data.findings || [];
  let parts = [];
  
  // Define all STU regions with their normal/abnormal states
  const stuRegions = [
    { normal: 'head_normal', abnormal: 'head_injury' },
    { normal: 'neck_normal', abnormal: 'neck_pain' },
    { normal: 'chest_normal', abnormal: 'chest_injury' },
    { normal: 'abdomen_normal', abnormal: 'abdomen_pain' },
    { normal: 'pelvis_normal', abnormal: 'pelvis_unstable' },
    { normal: 'spine_normal', abnormal: 'spine_pain' },
    { normal: 'extremities_normal', abnormal: 'extremities_injury' }
  ];
  
  // For each region, check if abnormality is selected, otherwise use normal
  stuRegions.forEach(region => {
    if (findings.includes(region.abnormal)) {
      // Abnormality selected
      parts.push(stuTexts[region.abnormal]);
    } else {
      // Default to normal
      parts.push(stuTexts[region.normal]);
    }
  });
  
  return '\nSTU - Schnelle Trauma-Untersuchung:\n' + parts.join(', ') + '.\n';
}

function generateMeasures(data) {
  if (!data) {
    return '';
  }
  
  let parts = [];
  const measures = data.measures || [];
  
  // Add selected measures
  measures.forEach(id => {
    if (measuresTexts[id]) {
      let text = measuresTexts[id];
      
      // Add oxygen flow rate if oxygen is given
      if ((id === 'oxygen_mask' || id === 'oxygen_nasal') && data.oxygen_flow) {
        text += ` (${data.oxygen_flow} L/min)`;
      }
      
      // Add medication details
      if (id === 'medication_given' && data.medication_details) {
        text += `: ${data.medication_details}`;
      }
      
      // Add wound care details
      if (id === 'wound_care' && data.wound_details) {
        text += `: ${data.wound_details}`;
      }
      
      // Add splint details
      if (id === 'fracture_splint' && data.splint_details) {
        text += `: ${data.splint_details}`;
      }
      
      parts.push(text);
    }
  });
  
  // Add additional measures free text
  if (data.additional_measures) {
    parts.push(data.additional_measures);
  }
  
  return parts.length > 0 ? '\nDurchgeführte Maßnahmen:\n' + parts.join('\n') + '\n' : '';
}

function generateAdditional(data, context = {}) {
  if (!data) return '';
  
  let parts = [];
  
  if (data.ivAccess) {
    parts.push('\n' + (context.impaired || context.confusion || context.orientationIncomplete
      ? 'Ein venöser Zugang wurde gelegt. Angaben prüfen: Aufklärung und Einwilligung bei eingeschränktem Bewusstsein gesondert dokumentieren.'
      : additionalTexts.ivAccess));
  }
  
  if (data.ambulatoryCare && data.transportRefusal) {
    parts.push('\nAngaben prüfen: Ambulante Versorgung und Transportverweigerung sind gleichzeitig ausgewählt. Bitte die zutreffende Versorgungsentscheidung auswählen.');
  } else if (data.ambulatoryCare) {
    parts.push('\n' + (context.impaired || context.confusion || context.orientationIncomplete
      ? 'Ambulante Versorgung ausgewählt. Angaben prüfen: Bei dokumentierter Bewusstseins- oder Orientierungsstörung müssen Transportbedarf, Aufklärungsfähigkeit und das weitere Vorgehen gesondert beurteilt und dokumentiert werden.'
      : additionalTexts.ambulatoryCare));
  } else if (data.transportRefusal) {
    parts.push('\n' + (context.impaired || context.confusion || context.orientationIncomplete
      ? 'Transportverweigerung ausgewählt. Angaben prüfen: Einwilligungsfähigkeit bei eingeschränktem Bewusstsein oder Verwirrtheit gesondert beurteilen und dokumentieren.'
      : additionalTexts.transportRefusal));
  }
  
  return parts.length > 0 ? parts.join('\n') : '';
}
